/**
 * NS: お気に入り（いいね）機能。設定は snippets/ns-fav-config.liquid の #ns-fav-config。
 *
 * - ハート（[data-ns-fav]）を押すと、その場で見た目を切り替えて保存する
 * - ログインしていない人：この端末の localStorage にだけ保存する
 * - ログイン中（GAS の設定済み）：変更を「送信待ち」に積んで GAS へ送り、GAS が顧客メタフィールドに保存する。
 *   送れなかった分は localStorage に残し、次に開いたときや一定時間後に送り直す。
 *   ページを開いたときは「サーバーの内容 ＋ 送信待ち」を今のお気に入りとする
 * - ログインしたら、それまで端末に保存していた分をアカウントへ移す
 * - お気に入りページ（[data-ns-fav-page]）では、検索結果のカードを商品IDで取り寄せて新しい順に並べる
 */
(() => {
  const configEl = document.getElementById('ns-fav-config');
  if (!configEl) return;
  const config = JSON.parse(configEl.textContent);

  const MAX_ITEMS = 200;
  const GUEST_KEY = 'ns_fav_guest_v1';
  const member = Boolean(config.customer && config.endpoint);
  const queueKey = member ? `ns_fav_queue_${config.customer.id}` : null;

  const store = {
    read(key) {
      try {
        return JSON.parse(localStorage.getItem(key));
      } catch {
        return null;
      }
    },
    write(key, value) {
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch {}
    },
    remove(key) {
      try {
        localStorage.removeItem(key);
      } catch {}
    },
  };

  /** @type {Map<string, {p: number, c: string, t: number, s?: number}>} 商品ID → いいねした時の価格・通貨・時刻・売り切れに気づいた時刻 */
  const items = new Map();
  let notify = true;
  let queue = [];

  function applyOp(op) {
    if (op.a === 'add') {
      items.set(String(op.id), { p: op.p, c: op.c, t: op.t });
      trimToMax();
    } else if (op.a === 'rm') {
      items.delete(String(op.id));
    } else if (op.a === 'sold') {
      // 売り切れに初めて気づいた時刻（0 は売り切れでなくなった＝取り消し）
      const item = items.get(String(op.id));
      if (item) {
        if (op.t) item.s = item.s || op.t;
        else delete item.s;
      }
    } else if (op.a === 'notify') {
      notify = Boolean(op.v);
    }
  }

  function trimToMax() {
    if (items.size <= MAX_ITEMS) return;
    const oldest = [...items.entries()].sort((a, b) => a[1].t - b[1].t).slice(0, items.size - MAX_ITEMS);
    for (const [id] of oldest) items.delete(id);
  }

  // --- 読み込み ---
  const guest = store.read(GUEST_KEY);
  if (member) {
    const data = config.customer.data || {};
    for (const [id, p, c, t, s] of data.items || []) items.set(String(id), s ? { p, c, t, s } : { p, c, t });
    if (data.notify === false) notify = false;
    queue = store.read(queueKey) || [];
    queue.forEach(applyOp);
    // ログイン前に端末へ保存していた分をアカウントへ移す
    if (guest?.items) {
      for (const [id, value] of Object.entries(guest.items)) {
        if (!items.has(id)) enqueue({ a: 'add', id, p: value.p, c: value.c, t: value.t });
      }
      store.remove(GUEST_KEY);
    }
  } else if (guest?.items) {
    for (const [id, value] of Object.entries(guest.items)) items.set(id, value);
  }

  // --- 保存 ---
  function enqueue(op) {
    applyOp(op);
    if (!member) {
      store.write(GUEST_KEY, { items: Object.fromEntries(items) });
      return;
    }
    // 同じ商品への操作は最後の1つだけ送れば足りる
    if (op.a === 'notify') queue = queue.filter((q) => q.a !== 'notify');
    else if (op.a === 'sold') queue = queue.filter((q) => !(q.a === 'sold' && String(q.id) === String(op.id)));
    else queue = queue.filter((q) => String(q.id) !== String(op.id));
    queue.push(op);
    store.write(queueKey, queue);
    flush();
  }

  let sending = false;
  let retryTimer = 0;
  let failures = 0;

  async function flush() {
    if (!member || sending || queue.length === 0) return;
    sending = true;
    const batch = queue.slice();
    try {
      // text/plain にすると事前確認の通信（CORS preflight）が要らない。GAS はリダイレクトしてから JSON を返す
      const response = await fetch(config.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({ cid: config.customer.id, sig: config.customer.sig, ops: batch }),
      });
      const result = await response.json();
      if (!result.ok) throw new Error(result.error || 'save failed');
      // 送っている間に増えた分は残す
      queue = queue.filter((op) => !batch.includes(op));
      store.write(queueKey, queue);
      failures = 0;
    } catch (error) {
      failures += 1;
      if (failures === 1) toast(config.t.error);
      clearTimeout(retryTimer);
      retryTimer = setTimeout(flush, Math.min(60000, 5000 * 2 ** failures));
    } finally {
      sending = false;
    }
    if (queue.length && failures === 0) flush();
  }

  function toggle(button) {
    const id = String(button.dataset.nsFav);
    if (items.has(id)) {
      enqueue({ a: 'rm', id });
      toast(config.t.removed);
    } else {
      enqueue({ a: 'add', id, p: Number(button.dataset.price) || 0, c: config.currency, t: Math.floor(Date.now() / 1000) });
      toast(config.t.added, true);
    }
    render();
  }

  // --- 表示 ---
  function render() {
    for (const button of document.querySelectorAll('[data-ns-fav]')) {
      const on = items.has(String(button.dataset.nsFav));
      if (button.getAttribute('aria-pressed') !== String(on)) {
        button.setAttribute('aria-pressed', String(on));
        if (button.classList.contains('ns-fav--card')) {
          const title = button.getAttribute('aria-label')?.split(': ').slice(1).join(': ') || '';
          button.setAttribute('aria-label', `${on ? config.t.heartRemove : config.t.heartAdd}: ${title}`);
        }
      }
    }
    for (const badge of document.querySelectorAll('[data-ns-fav-count]')) {
      // 中身が同じなら書き換えない（書き換えると下の MutationObserver が反応して、毎フレーム描き直しが続く）
      const text = items.size > 99 ? '99+' : String(items.size);
      if (badge.textContent !== text) badge.textContent = text;
      badge.hidden = items.size === 0;
    }
    const pageCount = document.querySelector('[data-ns-fav-page-count]');
    const countText = config.t.count.replace('__N__', String(items.size));
    if (pageCount && pageCount.textContent !== countText) pageCount.textContent = countText;
    const notifyBox = document.querySelector('[data-ns-fav-notify]');
    if (notifyBox) notifyBox.checked = notify;
  }

  let toastEl = null;
  let toastTimer = 0;
  function toast(message, withLink = false) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'ns-fav-toast';
      toastEl.setAttribute('role', 'status');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    if (withLink && !document.querySelector('[data-ns-fav-page]')) {
      const link = document.createElement('a');
      link.href = config.pageUrl;
      link.textContent = config.t.view;
      toastEl.appendChild(link);
    }
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-visible'), 2600);
  }

  // カードの中のボタンは、カード全体のリンクより先に受け取って止める
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest?.('[data-ns-fav]');
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      toggle(button);
    },
    true
  );

  document.addEventListener('change', (event) => {
    if (event.target.matches?.('[data-ns-fav-notify]')) enqueue({ a: 'notify', v: event.target.checked });
  });

  // 絞り込み・並べ替え・「さらに表示する」などで後から増えたカードにも反映する
  let pending = false;
  new MutationObserver(() => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      render();
    });
  }).observe(document.body, { childList: true, subtree: true });

  // 別のタブで変えたときも合わせる（ログインしていない人）
  window.addEventListener('storage', (event) => {
    if (member || event.key !== GUEST_KEY) return;
    items.clear();
    for (const [id, value] of Object.entries(store.read(GUEST_KEY)?.items || {})) items.set(id, value);
    render();
  });

  // --- お気に入りページ ---
  const CARD_BATCH_SIZE = 12;
  const SOLD_KEEP_DAYS = 30;

  async function fetchText(url) {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.text();
  }

  function searchUrl(ids, sectionId) {
    const url = new URL(`${window.Shopify?.routes?.root ?? '/'}search`, window.location.origin);
    url.searchParams.set('type', 'product');
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    if (sectionId) url.searchParams.set('section_id', sectionId);
    return url;
  }

  /**
   * 検索結果のセクションを、カードの一覧（ul.product-grid）までの骨組みだけにして複製する。
   * 一覧の並び方や重ね表示の見た目は、セクションの id とクラスに結びついたスタイルで決まるため
   */
  function gridSkeleton(sectionEl) {
    const grid = sectionEl.querySelector('ul.product-grid');
    if (!grid) return null;
    let built = null;
    let gridClone = null;
    for (let node = grid; node && node !== sectionEl.parentNode; node = node.parentElement) {
      const clone = node.cloneNode(false);
      if (built) clone.appendChild(built);
      else gridClone = clone;
      built = clone;
    }
    for (const style of sectionEl.querySelectorAll('style')) built.appendChild(style.cloneNode(true));
    // 検索ページでは並べ替えが済むまで後ろのカードを隠すスタイルがあるので、済んだ扱いにする
    built.querySelector('results-list')?.setAttribute('data-ns-arranged', '');
    return { root: built, grid: gridClone };
  }

  async function renderPage() {
    const page = document.querySelector('[data-ns-fav-page]');
    if (!page) return;
    const list = page.querySelector('[data-ns-fav-list]');
    const empty = page.querySelector('[data-ns-fav-empty]');
    const ids = [...items.entries()].sort((a, b) => b[1].t - a[1].t).map(([id]) => id);
    if (ids.length === 0) {
      list.hidden = true;
      empty.hidden = false;
      return;
    }

    const cards = new Map();
    const themeId = window.Shopify?.theme?.id ?? '';
    const sidKey = `ns_fav_sid_${themeId}`;
    let sectionId = store.read(sidKey);
    let skeleton = null;
    const batches = [];
    for (let i = 0; i < ids.length; i += CARD_BATCH_SIZE) batches.push(ids.slice(i, i + CARD_BATCH_SIZE));

    const collect = (doc) => {
      for (const card of doc.querySelectorAll('ul.product-grid > li[data-product-id]')) {
        cards.set(card.getAttribute('data-product-id'), card);
      }
      if (!skeleton) {
        const section = doc.querySelector('[id^="shopify-section-"][id$="__main"]');
        if (section) skeleton = gridSkeleton(section);
      }
    };

    try {
      // 検索結果のセクションの id はテーマごとに違うので、最初の1回だけページごと取得して調べ、覚えておく
      let rest = batches;
      if (!sectionId) {
        const html = await fetchText(searchUrl(batches[0]));
        const match = html.match(/id="shopify-section-(template--\d+__main)"/);
        if (match) {
          sectionId = match[1];
          store.write(sidKey, sectionId);
        }
        collect(new DOMParser().parseFromString(html, 'text/html'));
        rest = batches.slice(1);
      }
      await Promise.all(
        rest.map(async (batch) => {
          const html = await fetchText(searchUrl(batch, sectionId));
          collect(new DOMParser().parseFromString(html, 'text/html'));
        })
      );
    } catch (error) {
      // 覚えていた id が古い（テーマが変わった）ときは、次回やり直す
      store.remove(sidKey);
    }

    if (!skeleton || cards.size === 0) {
      list.hidden = true;
      empty.hidden = false;
      return;
    }

    // 売り切れの商品は、売り切れに気づいてから30日たったらお気に入りから外す
    const now = Math.floor(Date.now() / 1000);
    for (const id of ids) {
      const card = cards.get(id);
      if (!card) continue;
      const saved = items.get(id);
      const sold = Boolean(card.querySelector('.ns-card-badge--sold'));
      if (sold && saved && !saved.s) enqueue({ a: 'sold', id, t: now });
      else if (!sold && saved?.s) enqueue({ a: 'sold', id, t: 0 });
      if (sold && saved?.s && now - saved.s > SOLD_KEEP_DAYS * 86400) {
        enqueue({ a: 'rm', id });
        continue;
      }
      skeleton.grid.appendChild(document.importNode(card, true));
    }
    list.replaceChildren(skeleton.root);
    render();
  }

  render();
  flush();
  renderPage();
})();
